import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { copyTextToClipboard } from "@/utils/copyTextToClipboard";
import AppLayout from "@/components/feature/AppLayout";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import {
  deleteEmbedCorsOrigin,
  getEmbedContext,
  getEmbedCorsOrigins,
  getWidgetSettings,
  postEmbedCorsOrigin,
  postEmbedNativeSnippet,
  postEmbedSnippet,
  postWidgetBootstrap,
  putWidgetDefaults,
} from "@/api/widgetGeneratorApi";

const NATIVE_PLATFORMS = [
  ["android", "Android (Kotlin)"],
  ["ios", "iOS / macOS (Swift)"],
  ["windows", "Windows (C#)"],
];

// Platform-specific "wire it into your app" steps shown in the Native apps guidance block.
// The generated snippet is a code fragment (not a standalone file), so these steps explain
// exactly what each platform needs around it.
const NATIVE_GUIDANCE = {
  android: {
    language: "Kotlin",
    steps: [
      `Add the INTERNET permission to AndroidManifest.xml: <uses-permission android:name="android.permission.INTERNET" />`,
      `Paste the generated code inside an Activity's onCreate() after super.onCreate() and your own setContentView(...). Do not replace your layout with the WebView — the snippet overlays a FAB + panel.`,
      `Header minimize / X are handled in the snippet (closeChatWidget relay). Keep that block so those buttons hide the panel; re-opening the FAB posts openChatWidget so a session ended with X starts fresh.`,
    ],
  },
  ios: {
    language: "Swift",
    steps: [
      `Add "import WebKit" at the top of the file.`,
      `Paste SentroDeskCloseRelay once at file scope, then paste the rest into a UIViewController's viewDidLoad() (or an NSViewController on macOS).`,
      `The snippet already relays closeChatWidget / openChatWidget so header minimize and X hide the panel and reopen correctly.`,
    ],
  },
  windows: {
    language: "C#",
    steps: [
      `Add the WebView2 NuGet package (Microsoft.Web.WebView2) and ensure the Evergreen WebView2 Runtime is installed.`,
      `Paste the generated code into an async Loaded handler. Keep EnsureCoreWebView2Async() before navigating.`,
      `The snippet already relays closeChatWidget / openChatWidget so header minimize and X hide the panel and reopen correctly.`,
    ],
  },
};

const DEFAULT_DRAFT = {
  primary_color: "#0d8989",
  button_color: "#0d8989",
  header_text: "Chat Support",
  sub_header_text: "",
  widget_width: 480,
  widget_height: 680,
  button_size: 70,
  border_radius: 12,
  iframe_width: 380,
  iframe_height: 560,
  z_index: 2147483000,
  show_session_timer: true,
};

const NUMBER_FIELDS = new Set([
  "widget_width",
  "widget_height",
  "button_size",
  "border_radius",
  "iframe_width",
  "iframe_height",
  "z_index",
]);

function normalizeDefaults(draft) {
  const out = {};
  for (const [key, fallback] of Object.entries(DEFAULT_DRAFT)) {
    const raw = draft[key];
    if (key === "show_session_timer") {
      out[key] = Boolean(raw);
    } else if (NUMBER_FIELDS.has(key)) {
      const n = Number(raw);
      out[key] = Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
    } else {
      out[key] = raw == null ? "" : String(raw);
    }
  }
  return out;
}

function toDraft(defaults) {
  return { ...DEFAULT_DRAFT, ...(defaults && typeof defaults === "object" ? defaults : {}) };
}

function messageFromError(error, fallback = "Request failed.") {
  if (error instanceof Error) return error.message;
  return String(error || fallback);
}

function Chip({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200/80 bg-white px-3 py-2 shadow-sm ring-1 ring-slate-100 dark:border-slate-700/80 dark:bg-slate-800 dark:ring-slate-700/50">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">{label}</p>
      <p className="mt-0.5 break-all font-mono text-xs text-slate-800 dark:text-slate-100">{value || "-"}</p>
    </div>
  );
}

function Field({ id, label, children, hint }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-semibold text-slate-800">
        {label}
      </label>
      {children}
      {hint ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}

function Banner({ tone = "info", children }) {
  const classes =
    tone === "error"
      ? "border-red-200 bg-red-50 text-red-900"
      : tone === "success"
        ? "border-emerald-200 bg-emerald-50 text-emerald-900"
        : "border-slate-200 bg-slate-50 text-slate-700";
  return (
    <div className={`rounded-2xl border px-4 py-3 text-sm ${classes}`} role={tone === "error" ? "alert" : "status"}>
      {children}
    </div>
  );
}

export default function WidgetGeneratorPage() {
  const { activeWorkspaceId, activeWorkspace, loading: wsLoading } = useWorkspace();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [originBusy, setOriginBusy] = useState("");
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [context, setContext] = useState(null);
  const [draft, setDraft] = useState(DEFAULT_DRAFT);
  const [htmlSnippet, setHtmlSnippet] = useState("");
  const [origins, setOrigins] = useState([]);
  const [corsOrigin, setCorsOrigin] = useState("");
  const [copyDone, setCopyDone] = useState(false);
  const snippetRef = useRef(null);

  const [activeTab, setActiveTab] = useState("website");
  const [nativePlatform, setNativePlatform] = useState("android");
  const [nativeOrigin, setNativeOrigin] = useState("");
  const [nativeCode, setNativeCode] = useState("");
  const [nativeLoadUrl, setNativeLoadUrl] = useState("");
  const [nativeBusy, setNativeBusy] = useState(false);
  const [nativeCopyDone, setNativeCopyDone] = useState(false);
  const nativeSnippetRef = useRef(null);

  const normalizedDraft = useMemo(() => normalizeDefaults(draft), [draft]);
  const nativeGuidance = NATIVE_GUIDANCE[nativePlatform] || NATIVE_GUIDANCE.android;

  const setDraftValue = (key, value) => {
    setDraft((current) => ({ ...current, [key]: value }));
  };

  const buildSnippet = useCallback(async (overrides = null) => {
    const workspaceName = String(activeWorkspace?.name || "").trim();
    const mergedOverrides = {
      ...(overrides && typeof overrides === "object" ? overrides : {}),
      ...(workspaceName ? { workspace_name: workspaceName } : {}),
    };
    const body = Object.keys(mergedOverrides).length ? { overrides: mergedOverrides } : {};
    const response = await postEmbedSnippet(body);
    if (!response?.success || !response?.data?.html) {
      throw new Error(response?.detail?.message || response?.message || "Could not build embed snippet.");
    }
    setHtmlSnippet(String(response.data.html));
  }, [activeWorkspace?.name]);

  const loadPage = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      let settings = await getWidgetSettings();
      if (!settings?.success) {
        throw new Error("Could not load widget settings.");
      }
      if (settings.data == null || settings.message === "not_initialized") {
        await postWidgetBootstrap({});
        settings = await getWidgetSettings();
      }
      if (!settings?.success || settings.data == null) {
        throw new Error("Widget bootstrap did not initialize settings.");
      }
      const nextDraft = toDraft(settings.data.widget_defaults);
      setDraft(nextDraft);

      const ctxRes = await getEmbedContext();
      if (!ctxRes?.success || !ctxRes?.data) {
        throw new Error(ctxRes?.detail?.message || "Could not load embed context.");
      }
      setContext(ctxRes.data);
      await buildSnippet(normalizeDefaults(nextDraft));

      const originRes = await getEmbedCorsOrigins();
      setOrigins(Array.isArray(originRes?.data?.origins) ? originRes.data.origins : []);
    } catch (e) {
      setContext(null);
      setHtmlSnippet("");
      setOrigins([]);
      setError(messageFromError(e));
    } finally {
      setLoading(false);
    }
  }, [activeWorkspaceId, buildSnippet]);

  useEffect(() => {
    if (wsLoading) return;
    if (!activeWorkspaceId) {
      setContext(null);
      setHtmlSnippet("");
      setOrigins([]);
      setError(null);
      return;
    }
    void loadPage();
  }, [activeWorkspaceId, wsLoading, loadPage]);

  useEffect(() => {
    if (origins.length === 0) {
      setNativeOrigin("");
      return;
    }
    setNativeOrigin((prev) => (prev && origins.includes(prev) ? prev : origins[0]));
  }, [origins]);

  const saveDefaults = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const saved = await putWidgetDefaults({ widget_defaults: normalizedDraft });
      if (!saved?.success) {
        throw new Error(saved?.detail?.message || "Could not save widget defaults.");
      }
      await buildSnippet(normalizedDraft);
      setNotice("Widget defaults saved and snippet refreshed.");
    } catch (e) {
      setError(messageFromError(e));
    } finally {
      setSaving(false);
    }
  };

  const previewSnippet = async () => {
    setPreviewing(true);
    setError(null);
    setNotice(null);
    try {
      await buildSnippet(normalizedDraft);
      setNotice("Preview snippet refreshed with current form values.");
    } catch (e) {
      setError(messageFromError(e));
    } finally {
      setPreviewing(false);
    }
  };

  const copyHtml = async () => {
    if (!htmlSnippet) return;
    setError(null);
    const ok = await copyTextToClipboard(htmlSnippet, { sourceElement: snippetRef.current });
    if (ok) {
      setCopyDone(true);
      setNotice("Snippet copied to clipboard.");
      setTimeout(() => setCopyDone(false), 2000);
      return;
    }
    const el = snippetRef.current;
    if (el) {
      el.focus();
      el.select();
      el.setSelectionRange(0, htmlSnippet.length);
    }
    setNotice("Snippet selected — press Ctrl+C (Cmd+C on Mac) to copy.");
  };

  const generateNative = async () => {
    if (!nativeOrigin) {
      setError("Register and select an app origin first (under embedder origins below).");
      return;
    }
    setNativeBusy(true);
    setError(null);
    setNotice(null);
    try {
      const workspaceName = String(activeWorkspace?.name || "").trim();
      const res = await postEmbedNativeSnippet({
        platform: nativePlatform,
        embedder_origin: nativeOrigin,
        overrides: {
          ...normalizedDraft,
          ...(workspaceName ? { workspace_name: workspaceName } : {}),
        },
      });
      if (!res?.success || !res?.data?.code) {
        throw new Error(res?.detail?.message || res?.message || "Could not build native snippet.");
      }
      setNativeCode(String(res.data.code));
      setNativeLoadUrl(String(res.data.load_url || ""));
      setNotice("Native snippet generated.");
    } catch (e) {
      setNativeCode("");
      setNativeLoadUrl("");
      setError(messageFromError(e));
    } finally {
      setNativeBusy(false);
    }
  };

  const copyNative = async () => {
    if (!nativeCode) return;
    setError(null);
    const ok = await copyTextToClipboard(nativeCode, { sourceElement: nativeSnippetRef.current });
    if (ok) {
      setNativeCopyDone(true);
      setNotice("Native snippet copied to clipboard.");
      setTimeout(() => setNativeCopyDone(false), 2000);
      return;
    }
    const el = nativeSnippetRef.current;
    if (el) {
      el.focus();
      el.select();
      el.setSelectionRange(0, nativeCode.length);
    }
    setNotice("Snippet selected — press Ctrl+C (Cmd+C on Mac) to copy.");
  };

  const registerOrigin = async () => {
    const origin = corsOrigin.trim();
    if (!origin) {
      setError("Enter an origin such as https://www.example.com.");
      return;
    }
    setOriginBusy("add");
    setError(null);
    setNotice(null);
    try {
      const res = await postEmbedCorsOrigin({ origin });
      setOrigins(Array.isArray(res?.data?.origins) ? res.data.origins : []);
      setCorsOrigin("");
      setNotice("Embedder origin registered.");
    } catch (e) {
      setError(messageFromError(e));
    } finally {
      setOriginBusy("");
    }
  };

  const removeOrigin = async (origin) => {
    setOriginBusy(origin);
    setError(null);
    setNotice(null);
    try {
      const res = await deleteEmbedCorsOrigin({ origin });
      setOrigins(Array.isArray(res?.data?.origins) ? res.data.origins : []);
      setNotice("Embedder origin removed.");
    } catch (e) {
      setError(messageFromError(e));
    } finally {
      setOriginBusy("");
    }
  };

  return (
    <AppLayout>
      <div className="ui-page scrollbar-thin h-full overflow-y-auto">
        <header className="ui-shell sticky top-0 z-10">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-teal-700 dark:text-teal-300">Workspace</p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl dark:text-slate-100">Widget generator</h1>
            <p className="mt-1 max-w-3xl text-sm leading-relaxed text-slate-500 dark:text-slate-400">
              Generate a workspace-scoped widget snippet. The embed must carry tenant id, realm name, and workspace id so
              public chatbot-widget calls resolve to the correct tenant without Keycloak tokens.
            </p>
          </div>
        </header>

        <div className="space-y-8 px-6 py-8 sm:px-8">
          {!activeWorkspaceId && !wsLoading ? (
            <Banner tone="info">Choose a workspace in the sidebar before generating embed code.</Banner>
          ) : null}

          {activeWorkspace ? (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Active workspace: <span className="font-medium text-slate-900 dark:text-slate-100">{activeWorkspace.name || activeWorkspace.id}</span>
            </p>
          ) : null}

          {error ? <Banner tone="error">{error}</Banner> : null}
          {notice ? <Banner tone="success">{notice}</Banner> : null}
          {loading && activeWorkspaceId ? <Banner>Loading widget generator...</Banner> : null}

          {context && activeWorkspaceId ? (
            <section className="space-y-3">
              <h2 className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Required embed identity</h2>
              <div className="grid gap-3 sm:grid-cols-3">
                <Chip label="Tenant id" value={String(context.tenant_id ?? "")} />
                <Chip label="Realm" value={String(context.realm_name ?? "")} />
                <Chip label="Workspace id" value={String(context.workspace_id ?? activeWorkspaceId ?? "")} />
              </div>
            </section>
          ) : null}

          {activeWorkspaceId ? (
            <>
            <div className="flex w-fit gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1 dark:border-slate-700 dark:bg-slate-800/70" role="tablist" aria-label="Embed target">
              {[["website", "Website"], ["native", "Native apps"]].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === id}
                  onClick={() => setActiveTab(id)}
                  className={`rounded-lg px-4 py-1.5 text-sm font-semibold transition ${activeTab === id ? "bg-white text-slate-900 shadow-sm dark:bg-slate-900 dark:text-slate-100" : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"}`}
                >
                  {label}
                </button>
              ))}
            </div>

            {activeTab === "native" ? (
              <section className="ui-card-static space-y-2 rounded-2xl p-6">
                <h2 className="text-lg font-semibold text-slate-900">How to embed in a native app</h2>
                <p className="text-sm text-slate-500">
                  The generated snippet is a code fragment that goes inside a WebView screen — not a standalone file. Follow these steps:
                </p>
                <ol className="mt-1 list-decimal space-y-1.5 pl-5 text-sm leading-relaxed text-slate-600">
                  <li>
                    Register your app's origin under <span className="font-medium text-slate-800">Registered embedder origins</span> below
                    (e.g. <code className="rounded bg-slate-200 px-1 text-slate-800 dark:bg-slate-700 dark:text-slate-100">https://app.yourcompany.com</code>),
                    then select it as the <span className="font-medium text-slate-800">App origin</span>.
                  </li>
                  <li>Choose your platform, click <span className="font-medium text-slate-800">Generate code</span>, then <span className="font-medium text-slate-800">Copy code</span> — the load URL already has your tenant, workspace, realm, origin, and theming baked in.</li>
                  <li>
                    Wire it into your app (<span className="font-medium text-slate-800">{nativeGuidance.language}</span>):
                    <ol className="mt-1 list-[lower-alpha] space-y-1 pl-5">
                      {nativeGuidance.steps.map((step, i) => (
                        <li key={i}>{step}</li>
                      ))}
                    </ol>
                  </li>
                  <li>Run the app — the chat panel loads full-bleed. Make sure the device/PC can reach the widget host and gateway.</li>
                </ol>
                <p className="text-xs text-slate-500">
                  The snippet already enables JavaScript + persistent DOM storage (required for chat sessions). Keep the App origin
                  registered, or chat-service will reject the widget's requests.
                </p>
              </section>
            ) : null}

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(360px,0.9fr)]">
              <section className="ui-card-static space-y-6 rounded-2xl p-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Widget appearance</h2>
                    <p className="mt-1 text-sm text-slate-500">These values are stored per workspace and rendered as safe data attributes.</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={previewSnippet} disabled={previewing || loading} className="ui-btn-secondary text-sm">
                      {previewing ? "Previewing..." : "Preview snippet"}
                    </button>
                    <button type="button" onClick={saveDefaults} disabled={saving || loading} className="ui-btn-primary text-sm">
                      {saving ? "Saving..." : "Save defaults"}
                    </button>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field id="wg-primary" label="Primary color" hint="Also used for header and sub-header text in the widget.">
                    <input id="wg-primary" type="color" className="h-11 w-full rounded-xl border border-slate-200 bg-white p-1" value={draft.primary_color} onChange={(e) => setDraftValue("primary_color", e.target.value)} />
                  </Field>
                  <Field id="wg-button" label="Button color">
                    <input id="wg-button" type="color" className="h-11 w-full rounded-xl border border-slate-200 bg-white p-1" value={draft.button_color} onChange={(e) => setDraftValue("button_color", e.target.value)} />
                  </Field>
                  <Field id="wg-header" label="Header text">
                    <input id="wg-header" className="ui-input text-sm" value={draft.header_text} onChange={(e) => setDraftValue("header_text", e.target.value)} />
                  </Field>
                  <Field id="wg-subheader" label="Sub-header text">
                    <input id="wg-subheader" className="ui-input text-sm" value={draft.sub_header_text} onChange={(e) => setDraftValue("sub_header_text", e.target.value)} />
                  </Field>
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                  {[
                    ["widget_width", "Widget width"],
                    ["widget_height", "Widget height"],
                    ["button_size", "Button size"],
                    ["border_radius", "Border radius"],
                    ["iframe_width", "Iframe width"],
                    ["iframe_height", "Iframe height"],
                    ["z_index", "Z-index"],
                  ].map(([key, label]) => (
                    <Field key={key} id={`wg-${key}`} label={label}>
                      <input
                        id={`wg-${key}`}
                        type="number"
                        min="1"
                        className="ui-input text-sm"
                        value={draft[key]}
                        onChange={(e) => setDraftValue(key, e.target.value)}
                      />
                    </Field>
                  ))}
                  <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-800/60">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Show session timer</p>
                      <p className="mt-1 text-xs text-slate-500">Rendered as data-show-session-timer.</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={Boolean(draft.show_session_timer)}
                      onChange={(e) => setDraftValue("show_session_timer", e.target.checked)}
                      className="h-5 w-5"
                      aria-label="Show session timer"
                    />
                  </div>
                </div>
              </section>

              {activeTab === "website" ? (
                <section className="ui-card-static space-y-4 rounded-2xl p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900">HTML snippet</h2>
                      <p className="mt-1 text-sm text-slate-500">Paste this on an allowed site origin.</p>
                    </div>
                    <button type="button" onClick={copyHtml} disabled={!htmlSnippet} className="ui-btn-primary text-sm">
                      {copyDone ? "Copied" : "Copy HTML"}
                    </button>
                  </div>
                  <textarea
                    ref={snippetRef}
                    readOnly
                    value={htmlSnippet}
                    rows={10}
                    className="ui-input font-mono text-xs leading-relaxed"
                    spellCheck={false}
                    aria-label="Embeddable HTML snippet"
                    onFocus={(e) => e.target.select()}
                  />
                  <div className="grid gap-2 text-xs text-slate-500">
                    <p>Widget URL: <span className="font-mono text-slate-700">{context?.widget_base_url || "not configured"}</span></p>
                    <p>Embed JS: <span className="font-mono text-slate-700">{context?.embed_js_url || "not configured"}</span></p>
                  </div>
                </section>
              ) : (
                <section className="ui-card-static space-y-4 rounded-2xl p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900">Native app snippet</h2>
                      <p className="mt-1 text-sm text-slate-500">Paste into a WebView screen in your Android, iOS/macOS, or Windows app.</p>
                    </div>
                    <button type="button" onClick={copyNative} disabled={!nativeCode} className="ui-btn-primary text-sm">
                      {nativeCopyDone ? "Copied" : "Copy code"}
                    </button>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field id="wg-platform" label="Platform">
                      <select id="wg-platform" className="ui-input text-sm" value={nativePlatform} onChange={(e) => setNativePlatform(e.target.value)}>
                        {NATIVE_PLATFORMS.map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </Field>
                    <Field id="wg-native-origin" label="App origin" hint="Must be a registered embedder origin (see below).">
                      <select id="wg-native-origin" className="ui-input text-sm" value={nativeOrigin} onChange={(e) => setNativeOrigin(e.target.value)} disabled={!origins.length}>
                        {origins.length ? (
                          origins.map((o) => <option key={o} value={o}>{o}</option>)
                        ) : (
                          <option value="">No origins registered yet</option>
                        )}
                      </select>
                    </Field>
                  </div>
                  <div className="space-y-1.5">
                    <button type="button" onClick={generateNative} disabled={nativeBusy || !nativeOrigin} className="ui-btn-secondary text-sm">
                      {nativeBusy ? "Generating..." : "Generate code"}
                    </button>
                    {!origins.length ? (
                      <p className="text-xs text-amber-600 dark:text-amber-400">Register an app origin below first — it then appears in the App origin list and enables this button.</p>
                    ) : null}
                  </div>
                  {nativeCode ? (
                    <>
                      <textarea
                        ref={nativeSnippetRef}
                        readOnly
                        value={nativeCode}
                        rows={14}
                        className="ui-input font-mono text-xs leading-relaxed"
                        spellCheck={false}
                        aria-label="Native app snippet"
                        onFocus={(e) => e.target.select()}
                      />
                      <p className="break-all text-xs text-slate-500">Load URL: <span className="font-mono text-slate-700">{nativeLoadUrl}</span></p>
                    </>
                  ) : (
                    <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800/50">
                      Pick a platform and a registered app origin, then generate the code to paste into your app's chat screen.
                    </p>
                  )}
                </section>
              )}
            </div>
            </>
          ) : null}

          {activeWorkspaceId ? (
            <section className="ui-card-static space-y-4 rounded-2xl p-6">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Registered embedder origins</h2>
                <p className="mt-1 text-sm text-slate-500">Only these site origins may use this tenant workspace widget at runtime.</p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <input
                  type="url"
                  value={corsOrigin}
                  onChange={(e) => setCorsOrigin(e.target.value)}
                  placeholder="https://www.example.com"
                  className="ui-input flex-1 text-sm"
                  aria-label="Site origin for CORS"
                />
                <button type="button" onClick={registerOrigin} disabled={originBusy === "add"} className="ui-btn-primary text-sm">
                  {originBusy === "add" ? "Registering..." : "Register origin"}
                </button>
              </div>
              <div className="space-y-2">
                {origins.length ? (
                  origins.map((origin) => (
                    <div key={origin} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-800/60">
                      <span className="break-all font-mono text-xs text-slate-700">{origin}</span>
                      <button
                        type="button"
                        onClick={() => void removeOrigin(origin)}
                        disabled={originBusy === origin}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                      >
                        {originBusy === origin ? "Removing..." : "Remove"}
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800/50">
                    No origins registered yet. Register the site origin before using the snippet in production.
                  </p>
                )}
              </div>
            </section>
          ) : null}
        </div>
      </div>
    </AppLayout>
  );
}
